import { postSearchReferenceTables } from "@/apis/reference-search/route";
import type { ReferenceTableHit } from "@/apis/reference-search/types";
import { Button } from "@/toggle-ui-kit/button";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState, type ClipboardEvent } from "react";

const RESULT_LIMIT = 30;

/**
 * 붙여넣기 한 번으로 표를 받는다. Excel·브라우저는 클립보드에 text/html 과 text/plain(TSV)을
 * 함께 실어 주는데, 병합 셀·헤더 정보는 html 에만 있어 html 을 우선하고 텍스트는 그대로
 * textarea 에 보인다. html 은 붙여넣은 텍스트가 바뀌면 버린다 — 손으로 고친 내용과 어긋나기 때문.
 */
export const QueryTableInput = ({
  mappedOnly,
  onResult,
}: {
  mappedOnly: boolean;
  onResult: (hits: ReferenceTableHit[], elapsedMs: number) => void;
}) => {
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [pastedHtml, setPastedHtml] = useState<string | null>(null);

  const search = useMutation({
    mutationFn: async () => {
      const started = performance.now();
      const response = await postSearchReferenceTables({
        title: title.trim() || null,
        html: pastedHtml,
        csv: pastedHtml ? null : text,
        limit: RESULT_LIMIT,
        mappedOnly,
      });
      return { hits: response.hits, elapsedMs: performance.now() - started };
    },
    onSuccess: ({ hits, elapsedMs }) => onResult(hits, elapsedMs),
  });

  const handlePaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const html = event.clipboardData.getData("text/html");
    setPastedHtml(html.includes("<table") ? html : null);
  };

  const canSearch = text.trim().length > 0 && !search.isPending;

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-neutral-200 p-4">
      <h2 className="label-1-normal-bold text-label-neutral">질의 표</h2>
      <label className="flex flex-col gap-1 text-label-neutral caption-1-normal-medium">
        주석 제목 (선택)
        <input
          id="reference-search-title"
          className="rounded-md border border-neutral-200 px-3 py-2 text-label-normal body-2-normal-medium"
          placeholder="예: 유형자산"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </label>
      <label className="flex flex-col gap-1 text-label-neutral caption-1-normal-medium">
        표 붙여넣기 — Excel·DART 화면에서 복사한 표, 또는 CSV
        <textarea
          id="reference-search-table"
          className="min-h-[240px] resize-y whitespace-pre rounded-md border border-neutral-200 px-3 py-2 font-mono text-xs text-label-normal"
          value={text}
          onPaste={handlePaste}
          onChange={(e) => {
            setText(e.target.value);
            setPastedHtml(null);
          }}
        />
      </label>
      <Button
        variant="solid"
        color="primary"
        size="base"
        disabled={!canSearch}
        onClick={() => search.mutate()}
        leftIcon={
          search.isPending ? <Loader2 className="size-4 animate-spin" /> : undefined
        }
      >
        유사한 주석 표 찾기
      </Button>
      {search.isError && (
        <p className="caption-1-normal-medium text-status-negative">
          검색에 실패했습니다. 표가 비어 있지 않은지 확인하고 다시 시도해 주세요.
        </p>
      )}
    </section>
  );
};
